import createAlgoliaHelper from './createAlgoliaHelper'
import config from '../config'

type Category = {
  objectID: string
  path: string
  active: boolean
  filters: Record<
    string,
    {
      label: string
      sorting: string
      filtertype: string
      unit?: string
    }
  >
  label: string
  hasSeries: boolean
  parent: Category | null
}

let categoriesPromise: Promise<Category[]> | null = null

async function doFetch() {
  const helper = await createAlgoliaHelper(config.index.categories, {
    facets: ['active']
  })
  helper.addFacetRefinement('active', 'true')
  const result: Category[] = []
  let page = 0

  while (true) {
    helper.setPage(page)
    const res = await helper.searchOnce({ hitsPerPage: 1000 })
    result.push(...res.content.hits)

    if (page < res.content.nbPages) {
      page++
    } else break
  }

  return result
}

/** @firescoutMockFn utils.categories_getAllCatgories */
export function getAllCategories() {
  if (categoriesPromise) return categoriesPromise
  categoriesPromise = doFetch()
  return categoriesPromise
}

/**
 * Retrieves all categories with their respective children. Categories without children will be included!
 * @returns A dictionary where the keys are the parent category objectIDs and the values are objects containing the category and its children. 
 * For example under the key 'barzubehoer1' one would find a similar value:
 * {
      category: {
        ...
        path: "Barzubehör > Cocktailzubereitung",
        ...
      },
      children: [
        {
          ...
          path: "Barzubehör > Cocktailzubereitung > Stößel",
          ...
        },
        {
          ...
          path: "Barzubehör > Cocktailzubereitung > Rührgläser",
          ...
        },
        {
          ...
          path: "Barzubehör > Cocktailzubereitung > Cocktail-Shaker",
          ...
        },
        ...
      ],
    }
 */
export async function getAllCategoriesAndTheirChildren() {
  const categories = await getAllCategories()

  const categoriesWithChildrenDict: Record<
    string,
    { category: Category; children: Category[] }
  > = {}

  for (const currentCategory of categories) {
    // Make sure that every category is in the dictionary
    if (!categoriesWithChildrenDict[currentCategory.objectID])
      categoriesWithChildrenDict[currentCategory.objectID] = {
        category: currentCategory,
        children: []
      }

    // Get the path of the parent category of this category
    const parentPath = currentCategory.path
      .split(' > ')
      .slice(0, -1)
      .join(' > ')

    // Find the parent category in the categories array
    const parentCategory = categories.find((c) => c.path === parentPath)
    if (!parentCategory) throw new Error('Parent category not found')

    const parentCategoryId = parentCategory.objectID

    // Assign the current category to its parent category
    if (!categoriesWithChildrenDict[parentCategoryId])
      categoriesWithChildrenDict[parentCategoryId] = {
        category: parentCategory,
        children: []
      }
    categoriesWithChildrenDict[parentCategoryId].children.push(currentCategory)
  }

  return categoriesWithChildrenDict
}

/**
 * Resolves the category from categories index. The request ist batched and cached. That means
 * that the request ist optimized for performance.
 */
export async function getCategory(objectID: string) {
  const categories = await getAllCategories()
  for (const cat of categories) {
    if (cat.objectID === objectID) return cat
  }
  return null
}

export function convertFilters(
  filterRaw: Record<
    string,
    { label: string; sorting: string; filtertype: string; unit?: string }
  >
) {
  const filters: {
    label: string
    key: string
    filtertype: string
    unit?: string
  }[] = []
  for (const filterKey in filterRaw) {
    filters.push({
      label: filterRaw[filterKey].label,
      key: filterKey,
      filtertype: filterRaw[filterKey].filtertype,
      unit: filterRaw[filterKey].unit
    })
  }

  return filters.sort((a, b) => {
    const scoreA = parseInt(filterRaw[a.key].sorting)
    const scoreB = parseInt(filterRaw[b.key].sorting)
    return scoreA > scoreB ? 1 : -1
  })
}

/**
 * Resolves the category filters in a format, that the listing index can handle it. If no categoryId
 * is given, the root filters are resolved
 */
export async function getFilters(categoryId?: string) {
  const hit = await getCategory(categoryId || config.rootCategoryId)
  if (!hit) return []

  return convertFilters(hit.filters)
}
